import type { AdvertisementCategory, ClassSession, ScreenSettings, SmartTimeRule } from '../shared.js';

export const DEFAULT_CATEGORY_WEIGHTS: Record<AdvertisementCategory, number> = {
  'Fuel Point': 30,
  'Events & Competitions': 25,
  'Memberships & Beginners': 20,
  'Daily Schedule': 15,
  'Branding & Sponsors': 10,
  'General Announcements': 0
};

export const DEFAULT_TIME_RULES: SmartTimeRule[] = [
  {
    id: 'weekday-morning',
    label: 'Morning',
    daysOfWeek: [1, 2, 3, 4, 5],
    startTime: '06:00',
    endTime: '12:00',
    categories: ['Fuel Point', 'Daily Schedule', 'Branding & Sponsors', 'General Announcements'],
    active: true
  },
  {
    id: 'weekday-afternoon',
    label: 'Afternoon',
    daysOfWeek: [1, 2, 3, 4, 5],
    startTime: '12:00',
    endTime: '17:00',
    categories: ['Memberships & Beginners', 'Fuel Point', 'Events & Competitions'],
    active: true
  },
  {
    id: 'weekday-evening',
    label: 'Evening',
    daysOfWeek: [1, 2, 3, 4, 5],
    startTime: '17:00',
    endTime: '23:00',
    categories: ['Memberships & Beginners', 'Fuel Point', 'Events & Competitions'],
    active: true
  },
  {
    id: 'weekend',
    label: 'Weekend',
    daysOfWeek: [0, 6],
    startTime: '06:00',
    endTime: '23:59',
    categories: ['Events & Competitions', 'Branding & Sponsors', 'General Announcements'],
    active: true
  }
];

export function defaultScreenSettings(screenId: string, updatedAt = new Date().toISOString()): ScreenSettings {
  return {
    screenId,
    mode: 'manual',
    targetLoopSeconds: 90,
    categoryWeights: { ...DEFAULT_CATEGORY_WEIGHTS },
    timeRules: DEFAULT_TIME_RULES.map((rule) => ({ ...rule, daysOfWeek: [...rule.daysOfWeek], categories: [...rule.categories] })),
    updatedAt
  };
}

const session = (id: string, dayOfWeek: number, startTime: string, endTime: string, title: string, detail: string, location: string): ClassSession => ({
  id, dayOfWeek, startTime, endTime, title, detail, location, active: true
});

// Verified from the supplied Elevation weekly timetable screenshots (October 2026).
export const VERIFIED_CLASS_SESSIONS: ClassSession[] = [
  session('mon-competition', 1, '10:30', '12:00', 'Competition Class', 'Invite Only', 'Mat 1'),
  session('mon-kids', 1, '16:30', '17:15', 'Kids 6-9 jaar', '', 'Mat 2'),
  session('mon-junior', 1, '17:15', '18:00', 'Junior 10-15 jaar', '', 'Mat 2'),
  session('mon-nogi-1', 1, '18:00', '19:00', 'No-Gi Level 1', '', 'Mat 1'),
  session('mon-beginner', 1, '18:15', '19:00', 'Beginner Course BJJ', '', 'Mat 2'),
  session('mon-nogi-3', 1, '19:00', '20:30', 'No-Gi Level 3', '', 'Mat 1'),
  session('mon-gi-1', 1, '19:00', '20:00', 'Gi Level 1', '', 'Mat 2'),
  session('mon-kickbox', 1, '19:15', '20:00', 'Kick Box', '', ''),
  session('mon-openmat', 1, '20:30', '21:00', 'Open Mat', '', 'Mat 1'),

  session('tue-competition', 2, '10:30', '12:00', 'Competition Class', 'Invite Only', 'Mat 1'),
  session('tue-kids', 2, '16:30', '17:15', 'Kids 6-9 jaar', '', 'Mat 2'),
  session('tue-junior', 2, '17:15', '18:00', 'Junior 10-15 jaar', '', 'Mat 2'),
  session('tue-nogi-1', 2, '18:00', '19:00', 'No-Gi Level 1', '', 'Mat 1'),
  session('tue-nogi-2', 2, '19:00', '20:00', 'No-Gi Level 2', '', 'Mat 1'),
  session('tue-kickbox', 2, '19:15', '20:00', 'Kick Box', '', ''),
  session('tue-mma', 2, '20:00', '21:00', 'MMA Class - Level 1', '', 'Mat 1'),

  session('wed-competition', 3, '10:30', '12:00', 'Competition Class', 'Invite Only', 'Mat 1'),
  session('wed-lunch', 3, '12:00', '13:00', 'Lunch Class', 'Level 1', 'Mat 1'),
  session('wed-mini', 3, '16:00', '16:30', 'Mini 3-5 jaar', '', 'Mat 1'),
  session('wed-kids', 3, '16:30', '17:15', 'Kids 6-9 jaar', '', 'Mat 2'),
  session('wed-junior', 3, '17:15', '18:00', 'Junior 10-15 jaar', '', 'Mat 2'),
  session('wed-nogi-1', 3, '18:00', '19:00', 'No-Gi Level 1', '', 'Mat 1'),
  session('wed-beginner', 3, '18:15', '19:00', 'Beginner Course BJJ', '', 'Mat 2'),
  session('wed-nogi-3', 3, '19:00', '20:30', 'No-Gi Level 3', '', 'Mat 1'),
  session('wed-gi-1', 3, '19:00', '20:00', 'Gi Level 1', '', 'Mat 2'),
  session('wed-kickbox', 3, '19:15', '20:00', 'Kick Box', '', ''),
  session('wed-openmat', 3, '20:30', '21:00', 'Open Mat', '', 'Mat 1'),

  session('thu-6am', 4, '06:00', '07:00', '6 AM Level 1', 'No-Gi', 'Mat 1'),
  session('thu-competition', 4, '10:30', '12:00', 'Competition Class', 'Invite Only', 'Mat 1'),
  session('thu-nogi-1', 4, '18:00', '19:00', 'No-Gi Level 1', '', 'Mat 1'),
  session('thu-nogi-2', 4, '19:00', '20:00', 'No-Gi Level 2', '', 'Mat 1'),
  session('thu-kickbox', 4, '19:15', '20:00', 'Kick Box', '', ''),
  session('thu-mma', 4, '20:00', '21:00', 'MMA Class - Level 1', '', 'Mat 1'),

  session('fri-competition', 5, '10:30', '12:00', 'Competition Class', 'Invite Only', 'Mat 1'),
  session('fri-lunch', 5, '12:00', '13:00', 'Lunch Class', 'Level 1', 'Mat 1'),
  session('fri-kids', 5, '16:30', '17:15', 'Kids 6-9 jaar', '', 'Mat 2'),
  session('fri-junior', 5, '17:15', '18:00', 'Junior 10-15 jaar', '', 'Mat 2'),
  session('fri-rounds', 5, '18:15', '19:15', '10 Round Friday', '', 'Mat 1'),

  session('sat-mini', 6, '11:00', '11:30', 'Mini 3-5 jaar', '', 'Mat 1'),
  session('sat-nogi', 6, '12:00', '13:00', 'No-Gi Level 1', '', 'Mat 1'),
  session('sat-youth-comp', 6, '13:00', '14:30', 'Youth Comp Class', '', 'Mat 1'),
  session('sat-openmat', 6, '13:00', '14:00', 'Open Mat', '', 'Mat 1'),
  session('sat-opengym', 6, '13:00', '14:00', 'Open Gym', '', ''),

  session('sun-wrestling', 0, '12:00', '13:00', 'Wrestling for BJJ', 'Level 1', 'Mat 1'),
  session('sun-openmat', 0, '13:00', '14:00', 'Open Mat', '', 'Mat 1'),
  session('sun-opengym', 0, '13:00', '14:00', 'Open Gym', '', '')
];
